import {
  EntityDeltaPayload,
  EntityDestroyedPayload,
  EntityStatePayload,
  PayloadData,
  ServerMessagePayload,
} from './api';
import {
  createComponentFactory,
  CreateComponentUnion,
  GetDefaults,
} from './components/ComponentBase';
import { ChildOfData, ChildrenData, CommonRegistry } from './components/components';

export type ComponentMap<TRegistry extends CommonRegistry, ComponentUnion> = Map<
  keyof TRegistry,
  ComponentUnion
>;

/**
 * Options for World construction.
 */
export interface WorldOptions {
  /**
   * Whether to track mutations and generate payloads.
   * Default: true. Set to false for client-side worlds or testing.
   */
  trackingEnabled?: boolean;
}

export class World<TRegistry extends CommonRegistry & { readonly __defaults: object }> {
  private nextEntityId = 0;
  public entities: Map<number, ComponentMap<TRegistry, CreateComponentUnion<TRegistry>>>;
  private componentDefaults: GetDefaults<TRegistry>;

  public readonly createComponent;

  /**
   * Whether mutation tracking is enabled.
   * When enabled, mutations generate payloads in pendingPayloads.
   */
  private trackingEnabled: boolean;

  /**
   * Pending payloads generated by mutations.
   * Call flushPendingPayloads() to drain this queue.
   */
  private pendingPayloads: ServerMessagePayload[] = [];

  /**
   * Per-entity destruction callbacks.
   * Systems register cleanup functions for specific entities.
   */
  private entityDestroyedCallbacks = new Map<number, Set<(entityId: number) => void>>();

  constructor(componentDefaults: GetDefaults<TRegistry>, options?: WorldOptions) {
    this.entities = new Map<number, ComponentMap<TRegistry, CreateComponentUnion<TRegistry>>>();
    this.componentDefaults = componentDefaults;
    this.createComponent = createComponentFactory<TRegistry, GetDefaults<TRegistry>>(
      this.componentDefaults,
    );
    this.trackingEnabled = options?.trackingEnabled ?? true;
  }

  // ---------------------------------------------------------------------------
  // Tracking Infrastructure
  // ---------------------------------------------------------------------------

  /**
   * Flush and return all pending payloads generated by mutations.
   * Clears the internal queue.
   */
  flushPendingPayloads(): ServerMessagePayload[] {
    const payloads = this.pendingPayloads;
    this.pendingPayloads = [];
    return payloads;
  }

  /**
   * Execute a function with tracking disabled.
   * Use for internal bookkeeping that shouldn't generate payloads.
   */
  withoutTracking<T>(fn: () => T): T {
    const wasEnabled = this.trackingEnabled;
    this.trackingEnabled = false;
    try {
      return fn();
    } finally {
      this.trackingEnabled = wasEnabled;
    }
  }

  /**
   * Check if tracking is currently enabled.
   */
  isTrackingEnabled(): boolean {
    return this.trackingEnabled;
  }

  /**
   * Enable or disable tracking.
   */
  setTrackingEnabled(enabled: boolean): void {
    this.trackingEnabled = enabled;
  }

  // ---------------------------------------------------------------------------
  // Entity ID Management
  // ---------------------------------------------------------------------------

  /**
   * Sets the next entity ID counter. Used when restoring state from persistence.
   * Should be set to max(existingEntityIds) + 1.
   */
  setNextEntityId(id: number): void {
    this.nextEntityId = id;
  }

  /**
   * Gets the current next entity ID value.
   */
  getNextEntityId(): number {
    return this.nextEntityId;
  }

  // ---------------------------------------------------------------------------
  // Entity Operations
  // ---------------------------------------------------------------------------

  createEntity(components: CreateComponentUnion<TRegistry>[] = []): number {
    // convert array of components into a map keys by the `typeName` property
    const newEntityComponents: ComponentMap<TRegistry, CreateComponentUnion<TRegistry>> = new Map<
      keyof TRegistry,
      CreateComponentUnion<TRegistry>
    >();

    for (const component of components) {
      newEntityComponents.set(component.typeName as keyof TRegistry, component);
    }

    const entityId = this.nextEntityId++;
    this.entities.set(entityId, newEntityComponents);

    // Track: generate EntityStatePayload
    if (this.trackingEnabled) {
      // Cast components to PayloadData for network serialization.
      // Components are serialized as-is (they're plain data objects with typeName).
      const payloadComponents: { [key: string]: PayloadData } = {};
      for (const component of components) {
        payloadComponents[component.typeName as string] = component as PayloadData;
      }
      const payload: EntityStatePayload = {
        type: 'entityState',
        entityId,
        components: payloadComponents,
        reason: 'created',
      };
      this.pendingPayloads.push(payload);
    }

    return entityId;
  }

  destroyEntity(entityId: number): void {
    // Fire per-entity destruction callbacks before any cleanup
    const callbacks = this.entityDestroyedCallbacks.get(entityId);
    if (callbacks) {
      for (const cb of callbacks) {
        cb(entityId);
      }
      this.entityDestroyedCallbacks.delete(entityId);
    }

    // Recursively destroy all children.
    // Children generate their own destruction payloads so clients can clean up properly.
    const childrenComp = this.getComponent(entityId, 'Children') as
      | ({ readonly typeName: 'Children' } & ChildrenData)
      | undefined;
    if (childrenComp) {
      // Create a copy to avoid mutation during iteration
      const childIds = [...childrenComp.children];
      for (const childId of childIds) {
        this.destroyEntity(childId);
      }
    }

    // Remove this entity from its parent's Children list (without tracking - this is
    // internal bookkeeping, not a state change that clients need to know about)
    const childOf = this.getComponent(entityId, 'ChildOf') as
      | ({ readonly typeName: 'ChildOf' } & ChildOfData)
      | undefined;
    if (childOf) {
      this.withoutTracking(() => {
        const parentChildren = this.getComponent(childOf.parent, 'Children') as
          | ({ readonly typeName: 'Children' } & ChildrenData)
          | undefined;
        if (parentChildren) {
          parentChildren.children = parentChildren.children.filter((id) => id !== entityId);
          // Remove component if empty
          if (parentChildren.children.length === 0) {
            this.removeComponent(childOf.parent, 'Children');
          }
        }
      });
    }

    // Finally delete the entity
    this.entities.delete(entityId);

    // Track: generate EntityDestroyedPayload
    if (this.trackingEnabled) {
      const payload: EntityDestroyedPayload = {
        type: 'entityDestroyed',
        entityId,
      };
      this.pendingPayloads.push(payload);
    }
  }

  getEntity(
    entityId: number,
  ): ComponentMap<TRegistry, CreateComponentUnion<TRegistry>> | undefined {
    return this.entities.get(entityId);
  }

  getAllEntities(): Map<number, ComponentMap<TRegistry, CreateComponentUnion<TRegistry>>> {
    return this.entities;
  }

  /**
   * Adds an entity with the specified ID. Returns true if the entity was added, false
   * if it already existed.
   * Note: This does NOT generate an EntityStatePayload. Use createEntity() for tracked creation.
   */
  addEntity(entityId: number): boolean {
    if (!this.entities.has(entityId)) {
      this.entities.set(entityId, new Map<keyof TRegistry, CreateComponentUnion<TRegistry>>());
      return true;
    }
    return false;
  }

  resetWorld(): void {
    this.entities.clear();
    this.nextEntityId = 0;
    this.pendingPayloads = [];
    this.entityDestroyedCallbacks.clear();
  }

  // ---------------------------------------------------------------------------
  // Entity Destruction Callbacks
  // ---------------------------------------------------------------------------

  /**
   * Register a callback to run when a specific entity is destroyed.
   * Use this to clean up resources tied to an entity (e.g., PixiJS objects).
   * @param entityId - The entity to watch
   * @param callback - Function to call when entity is destroyed, receives entityId
   * @returns Unregister function
   */
  registerOnEntityDestroyed(entityId: number, callback: (entityId: number) => void): () => void {
    let callbacks = this.entityDestroyedCallbacks.get(entityId);
    if (!callbacks) {
      callbacks = new Set();
      this.entityDestroyedCallbacks.set(entityId, callbacks);
    }
    callbacks.add(callback);

    return () => {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.entityDestroyedCallbacks.delete(entityId);
      }
    };
  }

  // ---------------------------------------------------------------------------
  // Component Operations
  // ---------------------------------------------------------------------------

  addComponent<K extends keyof TRegistry>(
    entityId: number,
    component: { readonly typeName: K } & TRegistry[K],
  ): void {
    const entity = this.entities.get(entityId);
    if (!entity) {
      console.warn(
        `World.addComponent: Entity ${entityId} does not exist. ` +
          `Cannot add component '${String(component.typeName)}'.`,
      );
      return;
    }

    entity.set(component.typeName, component);

    // Track: generate EntityDeltaPayload with 'add'
    // Cast to PayloadData for network serialization (components are plain data objects).
    if (this.trackingEnabled) {
      const payload: EntityDeltaPayload = {
        type: 'entityDelta',
        entityId,
        add: { [component.typeName as string]: component as PayloadData },
      };
      this.pendingPayloads.push(payload);
    }
  }

  removeComponent(entityId: number, typeName: keyof TRegistry): void {
    const entityComponents = this.entities.get(entityId);
    if (entityComponents && entityComponents.has(typeName)) {
      entityComponents.delete(typeName);

      // Track: generate EntityDeltaPayload with 'remove'
      if (this.trackingEnabled) {
        const payload: EntityDeltaPayload = {
          type: 'entityDelta',
          entityId,
          remove: [typeName as string],
        };
        this.pendingPayloads.push(payload);
      }
    }
  }

  updateComponent<K extends keyof TRegistry>(
    entityId: number,
    component: { readonly typeName: K } & TRegistry[K],
    args: Partial<TRegistry[K]>,
  ): void {
    // Validate that the component belongs to the specified entity.
    // This prevents incorrect payloads if caller passes wrong entityId.
    const entity = this.entities.get(entityId);
    if (!entity) {
      console.warn(
        `World.updateComponent: Entity ${entityId} does not exist. ` +
          `Cannot update component '${String(component.typeName)}'.`,
      );
      return;
    }

    const storedComponent = entity.get(component.typeName);
    if (storedComponent !== component) {
      console.warn(
        `World.updateComponent: Component '${String(component.typeName)}' does not belong ` +
          `to entity ${entityId}. Payload may be incorrect.`,
      );
    }

    Object.assign(component, args);

    // Track: generate EntityDeltaPayload with 'update'
    // Only the changed fields (args) are serialized, not the entire component.
    if (this.trackingEnabled) {
      const payload: EntityDeltaPayload = {
        type: 'entityDelta',
        entityId,
        update: { [component.typeName as string]: args as PayloadData },
      };
      this.pendingPayloads.push(payload);
    }
  }

  hasComponent(entityId: number, typeName: keyof TRegistry): boolean {
    const entityComponents = this.entities.get(entityId);
    return entityComponents ? entityComponents.has(typeName) : false;
  }

  getComponent<K extends keyof TRegistry>(
    entityId: number,
    typeName: K,
  ): ({ readonly typeName: K } & TRegistry[K]) | undefined {
    const entity = this.entities.get(entityId);
    if (!entity) {
      return undefined;
    }

    return entity.get(typeName) as ({ readonly typeName: K } & TRegistry[K]) | undefined;
  }

  // ---------------------------------------------------------------------------
  // Parent/Child Relationships
  // ---------------------------------------------------------------------------

  /**
   * Add children to a parent entity.
   * - Adds ChildOf component to each child pointing to the parent
   * - Creates/updates Children component on parent with child IDs
   *
   * Note: Uses withoutTracking for internal bookkeeping to avoid noisy payloads.
   *
   * @param parentId - The parent entity ID
   * @param childIds - Array of child entity IDs to add
   */
  addChildren(parentId: number, childIds: number[]): void {
    if (!this.entities.has(parentId)) {
      return;
    }

    // Determine the root for the children:
    // If parent has a ChildOf, use its root. Otherwise, parent IS the root.
    const parentChildOf = this.getComponent(parentId, 'ChildOf') as
      | ({ readonly typeName: 'ChildOf' } & ChildOfData)
      | undefined;
    const rootId = parentChildOf?.root ?? parentId;

    this.withoutTracking(() => {
      // Get or create Children component on parent
      let childrenComp = this.getComponent(parentId, 'Children') as
        | ({ readonly typeName: 'Children' } & ChildrenData)
        | undefined;

      if (!childrenComp) {
        childrenComp = {
          typeName: 'Children' as const,
          children: [],
        } as { readonly typeName: 'Children' } & ChildrenData;
        this.addComponent(parentId, childrenComp);
      }

      for (const childId of childIds) {
        if (!this.entities.has(childId)) {
          continue;
        }

        // Check if child already has a different parent - remove from old parent first
        const existingChildOf = this.getComponent(childId, 'ChildOf') as
          | ({ readonly typeName: 'ChildOf' } & ChildOfData)
          | undefined;

        if (existingChildOf && existingChildOf.parent !== parentId) {
          this.removeChildren(existingChildOf.parent, [childId]);
        }

        // Add/update ChildOf on child (with root tracking)
        if (
          !existingChildOf ||
          existingChildOf.parent !== parentId ||
          existingChildOf.root !== rootId
        ) {
          const childOfComp = {
            typeName: 'ChildOf' as const,
            parent: parentId,
            root: rootId,
          } as { readonly typeName: 'ChildOf' } & ChildOfData;
          this.addComponent(childId, childOfComp);
        }

        // Add to parent's children list if not already present
        if (!childrenComp.children.includes(childId)) {
          childrenComp.children.push(childId);
        }
      }
    });
  }

  /**
   * Remove children from a parent entity.
   * - Removes ChildOf component from each child
   * - Updates parent's Children component (removes the component if empty)
   *
   * Note: Uses withoutTracking for internal bookkeeping to avoid noisy payloads.
   *
   * @param parentId - The parent entity ID
   * @param childIds - Array of child entity IDs to remove
   */
  removeChildren(parentId: number, childIds: number[]): void {
    this.withoutTracking(() => {
      const childrenComp = this.getComponent(parentId, 'Children') as
        | ({ readonly typeName: 'Children' } & ChildrenData)
        | undefined;

      if (!childrenComp) {
        return;
      }

      for (const childId of childIds) {
        // Remove ChildOf from child
        this.removeComponent(childId, 'ChildOf');

        // Remove from parent's children list
        const index = childrenComp.children.indexOf(childId);
        if (index !== -1) {
          childrenComp.children.splice(index, 1);
        }
      }

      // Remove Children component if empty
      if (childrenComp.children.length === 0) {
        this.removeComponent(parentId, 'Children');
      }
    });
  }

  /**
   * Get the parent entity ID for a child, if it has one.
   *
   * @param childId - The child entity ID
   * @returns The parent entity ID, or undefined if no parent
   */
  getParent(childId: number): number | undefined {
    const childOf = this.getComponent(childId, 'ChildOf') as
      | ({ readonly typeName: 'ChildOf' } & ChildOfData)
      | undefined;
    return childOf?.parent;
  }

  /**
   * Get all direct children of an entity.
   *
   * @param parentId - The parent entity ID
   * @returns Array of child entity IDs, or empty array if no children
   */
  getChildren(parentId: number): number[] {
    const childrenComp = this.getComponent(parentId, 'Children') as
      | ({ readonly typeName: 'Children' } & ChildrenData)
      | undefined;
    return childrenComp?.children ?? [];
  }

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------

  /**
   * Query entities that have all of the specified components and none of the excluded components.
   *
   * @param componentClasses - List of component type names that matching entities must have.
   * @param exclude - Optional list of component type names that matching entities must not have.
   * @returns An array of entity IDs that match the inclusion and exclusion criteria.
   */
  query(componentClasses: (keyof TRegistry)[], exclude?: (keyof TRegistry)[]): number[] {
    const matchingEntities: number[] = [];
    for (const [entityId, components] of this.entities.entries()) {
      const hasAllRequired = componentClasses.every((componentName) =>
        components.has(componentName),
      );

      if (!hasAllRequired) {
        continue;
      }

      const hasAnyExcluded = exclude
        ? exclude.some((componentName) => components.has(componentName))
        : false;

      if (!hasAnyExcluded) {
        matchingEntities.push(entityId);
      }
    }
    return matchingEntities;
  }
}
